import {
    AuthError,
    createUserWithEmailAndPassword,
    fetchSignInMethodsForEmail,
    GoogleAuthProvider,
    onIdTokenChanged,
    reload,
    sendEmailVerification,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    signInWithPopup,
    User,
} from 'firebase/auth';
import { JcUnsubscribe } from 'jcsdk';
import { jcAuth } from '../JcFirebase';
import { JcAuthError, JcAuthProviderId } from './JcAuthError';

const GOOGLE_PROVIDER_ID = 'google.com';
const PASSWORD_PROVIDER_ID = 'password';

/** The signed-in Firebase user, as seen by an app's auth model factory. */
export interface JcAuthUser {
    readonly uid: string;
    readonly displayName: string | null;
    readonly photoURL: string | null;
    readonly email: string | null;
    readonly emailVerified: boolean;
}

export interface JcAuthServiceOptions<AuthT> {
    /** Builds the app's auth model from the user and their ID-token custom claims. */
    readonly toAuth: (user: JcAuthUser, claims: Readonly<Record<string, unknown>>) => AuthT;
    /** Send a verification email on password sign-up. */
    readonly requireEmailVerification?: boolean;
    /**
     * Path the verification link returns to, on the current origin (which must be
     * an authorized domain in the Firebase console).
     */
    readonly verifyEmailPath?: string;
}

/**
 * The single entry point for the Firebase Auth session: Google (popup) and
 * email/password sign-in as peers.
 *
 * Projects use "one account per email", so an email belongs to one provider.
 * Cross-provider attempts (password on a Google account, or Google on a password
 * account) surface as `wrong-provider` naming the existing provider. With Email
 * Enumeration Protection on, the provider lookup is empty and the generic
 * `invalid-credentials` / `email-already-in-use` error is thrown instead.
 */
export class JcAuthService<AuthT> {
    constructor(private readonly options: JcAuthServiceOptions<AuthT>) {}

    /**
     * Subscribes to sign-in/out and ID-token refreshes (so custom-claim changes
     * propagate), yielding the app's auth model or `null` when signed out.
     */
    onAuthChange(onNextAuth: (auth: AuthT | null) => void): JcUnsubscribe {
        // toAuth reads claims asynchronously; the ticket drops a slow resolve for
        // an older auth state so it can't overwrite a newer one.
        let latest = 0;
        return onIdTokenChanged(jcAuth(), async (user) => {
            const ticket = ++latest;
            const next = user === null ? null : await this.toAuth(user);
            if (ticket === latest) {
                onNextAuth(next);
            }
        });
    }

    /**
     * Re-fetches the current user so `emailVerified` reflects the server — the
     * listener doesn't refire when an email is verified out-of-band.
     */
    async reloadCurrentAuth(): Promise<AuthT | null> {
        const user = jcAuth().currentUser;
        if (!user) return null;
        await reload(user);
        return this.toAuth(user);
    }

    /** Forces a fresh ID token to pick up custom-claim changes; also refires `onAuthChange`. */
    async refreshClaims(): Promise<AuthT | null> {
        const user = jcAuth().currentUser;
        if (!user) return null;
        await user.getIdToken(true);
        return this.toAuth(user);
    }

    /** Sends (or resends) the verification email to the signed-in user. */
    async sendVerificationEmail(): Promise<void> {
        const user = jcAuth().currentUser;
        if (!user) return;
        try {
            await sendEmailVerification(user, this.verificationSettings());
        } catch (err) {
            throw this.mapGenericError(err);
        }
    }

    async signInWithGoogle(): Promise<void> {
        try {
            await signInWithPopup(jcAuth(), new GoogleAuthProvider());
        } catch (err) {
            throw this.mapPopupError(err);
        }
    }

    async signInWithEmailAndPassword(email: string, password: string): Promise<void> {
        const trimmed = email.trim();
        try {
            await signInWithEmailAndPassword(jcAuth(), trimmed, password);
        } catch (err) {
            throw await this.mapPasswordSignInError(err, trimmed);
        }
    }

    /** Creates an email/password account; app profile documents are created separately. */
    async signUpWithEmailAndPassword(email: string, password: string): Promise<void> {
        const trimmed = email.trim();
        let user: User;
        try {
            user = (await createUserWithEmailAndPassword(jcAuth(), trimmed, password)).user;
        } catch (err) {
            throw await this.mapPasswordSignUpError(err, trimmed);
        }
        // The account exists either way; the verify-email screen can resend.
        if (this.options.requireEmailVerification) {
            try {
                await sendEmailVerification(user, this.verificationSettings());
            } catch (err) {
                console.error('Failed to send verification email after sign-up:', err);
            }
        }
    }

    /**
     * Sends a password-reset email. Resolves whether or not the email is
     * registered (enumeration protection), so always show the same confirmation.
     */
    async sendPasswordReset(email: string): Promise<void> {
        try {
            await sendPasswordResetEmail(jcAuth(), email.trim());
        } catch (err) {
            throw this.mapGenericError(err);
        }
    }

    signOut(): Promise<void> {
        return jcAuth().signOut();
    }

    private async toAuth(user: User): Promise<AuthT> {
        const token = await user.getIdTokenResult();
        return this.options.toAuth(user, token.claims);
    }

    private verificationSettings() {
        return {
            url: `${window.location.origin}${this.options.verifyEmailPath ?? '/'}`,
            handleCodeInApp: false,
        };
    }

    private async mapPasswordSignInError(err: unknown, email: string): Promise<JcAuthError> {
        switch (firebaseErrorCode(err)) {
            case 'auth/invalid-email':
                return new JcAuthError('invalid-email', 'That email address looks invalid.');
            case 'auth/user-disabled':
                return new JcAuthError('invalid-credentials', 'This account has been disabled.');
            case 'auth/too-many-requests':
                return new JcAuthError('too-many-requests',
                    'Too many sign-in attempts. Please wait a moment and try again.');
            case 'auth/network-request-failed':
                return networkError();
            case 'auth/invalid-credential':
            case 'auth/wrong-password':
            case 'auth/user-not-found': {
                const otherProvider = await detectOtherProvider(email, PASSWORD_PROVIDER_ID);
                if (otherProvider) {
                    return wrongProviderError(otherProvider);
                }
                return new JcAuthError('invalid-credentials', 'Invalid email or password.');
            }
            default:
                return this.mapGenericError(err);
        }
    }

    private async mapPasswordSignUpError(err: unknown, email: string): Promise<JcAuthError> {
        switch (firebaseErrorCode(err)) {
            case 'auth/invalid-email':
                return new JcAuthError('invalid-email', 'That email address looks invalid.');
            case 'auth/weak-password':
                return new JcAuthError('weak-password',
                    'Password is too weak. Please choose at least 6 characters.');
            case 'auth/network-request-failed':
                return networkError();
            case 'auth/email-already-in-use': {
                const otherProvider = await detectOtherProvider(email, PASSWORD_PROVIDER_ID);
                if (otherProvider) {
                    return wrongProviderError(otherProvider);
                }
                return new JcAuthError('email-already-in-use',
                    'An account with this email already exists. Try signing in instead.');
            }
            default:
                return this.mapGenericError(err);
        }
    }

    private mapPopupError(err: unknown): JcAuthError {
        switch (firebaseErrorCode(err)) {
            case 'auth/popup-closed-by-user':
            case 'auth/cancelled-popup-request':
                return new JcAuthError('popup-closed', 'Sign-in was cancelled.');
            case 'auth/popup-blocked':
                return new JcAuthError('popup-closed',
                    'Your browser blocked the sign-in popup. Please allow popups and try again.');
            case 'auth/network-request-failed':
                return networkError();
            case 'auth/account-exists-with-different-credential':
                return wrongProviderError(PASSWORD_PROVIDER_ID);
            default:
                return this.mapGenericError(err);
        }
    }

    private mapGenericError(err: unknown): JcAuthError {
        const code = firebaseErrorCode(err);
        const message = (err instanceof Error && err.message) || 'Something went wrong. Please try again.';
        return new JcAuthError('unknown', code ? `${message} (${code})` : message);
    }
}

const networkError = (): JcAuthError =>
    new JcAuthError('network', 'Network error. Please check your connection and try again.');

const wrongProviderError = (provider: JcAuthProviderId): JcAuthError => {
    const message = provider === GOOGLE_PROVIDER_ID
        ? 'This account was created with Google. Please use the "Sign in with Google" button below.'
        : 'This account was created with a password. Please sign in with your email and password.';
    return new JcAuthError('wrong-provider', message, provider);
};

/** The first provider on `email` other than `excludeProvider`, or null if unknown. */
const detectOtherProvider = async (
    email: string,
    excludeProvider: JcAuthProviderId,
): Promise<JcAuthProviderId | null> => {
    try {
        const methods = await fetchSignInMethodsForEmail(jcAuth(), email);
        for (const method of methods) {
            if (method === GOOGLE_PROVIDER_ID && excludeProvider !== GOOGLE_PROVIDER_ID) return GOOGLE_PROVIDER_ID;
            if (method === PASSWORD_PROVIDER_ID && excludeProvider !== PASSWORD_PROVIDER_ID) return PASSWORD_PROVIDER_ID;
        }
        return null;
    } catch {
        return null;
    }
};

const isAuthError = (err: unknown): err is AuthError =>
    typeof err === 'object' && err !== null && 'code' in err && typeof err.code === 'string';

const firebaseErrorCode = (err: unknown): string | null => (isAuthError(err) ? err.code : null);
